import type { AlertSubscriptionDetails, AlertSubscriptionLocation } from "@/api/hooks/alert";
import type { Feature, FeatureCollection, Geometry, Polygon, Position } from "geojson";

export const emptyAlertGeoJson: FeatureCollection<Geometry> = {
  type: "FeatureCollection",
  features: [],
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function toPosition(value: unknown): Position | null {
  if (Array.isArray(value) && value.length >= 2 && isFiniteNumber(value[0]) && isFiniteNumber(value[1])) {
    return [value[0], value[1]];
  }

  if (typeof value === "object" && value !== null) {
    const point = value as Record<string, unknown>;
    const lat = point.lat ?? point.latitude ?? point.location_lat;
    const lon = point.lon ?? point.lng ?? point.longitude ?? point.location_lon;
    if (isFiniteNumber(lat) && isFiniteNumber(lon)) {
      return [lon, lat];
    }
  }

  return null;
}

function closeRing(ring: Position[]): Position[] | null {
  if (ring.length < 3) {
    return null;
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
}

function parsePolygonCoordinates(value: unknown): Polygon["coordinates"] | null {
  if (typeof value === "string") {
    try {
      return parsePolygonCoordinates(JSON.parse(value));
    } catch {
      const values = value.split(",").map(Number);
      if (values.length < 6 || values.length % 2 !== 0 || values.some((coordinate) => !Number.isFinite(coordinate))) {
        return null;
      }
      const ring = closeRing(
        Array.from({ length: values.length / 2 }, (_, index) => [values[index * 2], values[index * 2 + 1]]),
      );
      return ring ? [ring] : null;
    }
  }

  if (typeof value === "object" && value !== null) {
    const geoJson = value as Record<string, unknown>;
    if (geoJson.type === "Feature") {
      return parsePolygonCoordinates(geoJson.geometry);
    }
    if (geoJson.type === "Polygon") {
      return parsePolygonCoordinates(geoJson.coordinates);
    }
    if (geoJson.coordinates !== undefined) {
      return parsePolygonCoordinates(geoJson.coordinates);
    }
    if (geoJson.type !== undefined) {
      return null;
    }
  }

  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const firstPosition = toPosition(value[0]);
  if (firstPosition) {
    const ring = value.map(toPosition);
    const closedRing = ring.every((position): position is Position => position !== null) ? closeRing(ring) : null;
    return closedRing ? [closedRing] : null;
  }

  const rings = value.map((ring) => {
    if (!Array.isArray(ring)) {
      return null;
    }
    const positions = ring.map(toPosition);
    return positions.every((position): position is Position => position !== null) ? closeRing(positions) : null;
  });
  return rings.every((ring): ring is Position[] => ring !== null) ? rings : null;
}

function getPolygonValue(location: AlertSubscriptionLocation): unknown {
  return location.location_polygon ?? location.polygon ?? location.geometry;
}

export function alertSubscriptionToGeoJson(subscription: AlertSubscriptionDetails): FeatureCollection<Geometry> {
  const features: Feature<Geometry>[] = [];

  for (const location of subscription.locations ?? []) {
    const properties = { alert: location.location_label ?? subscription.name };

    if (isFiniteNumber(location.location_lon) && isFiniteNumber(location.location_lat)) {
      features.push({
        type: "Feature",
        properties,
        geometry: { type: "Point", coordinates: [location.location_lon, location.location_lat] },
      });
    }

    const polygonCoordinates = parsePolygonCoordinates(getPolygonValue(location));
    if (polygonCoordinates) {
      features.push({
        type: "Feature",
        properties,
        geometry: { type: "Polygon", coordinates: polygonCoordinates },
      });
    }
  }

  return { type: "FeatureCollection", features };
}
